import { Button, Group, Modal, Stack, Text, Textarea } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { notifications } from "@dehub/theme";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReturnPreventionAlertApi } from "../../apis";
import type { AlertStatus } from "../../apis/return-prevention-alert";
import { message } from "../../utils/message";
import { usePermission } from "../permission";

// ─── Action matrix ───────────────────────────────────────────────────────────

export type AlertAction = "acknowledge" | "mute" | "resolve";

/** Every prevention-alert action route asserts RETURN_MANAGER_OVERRIDE (routes/api/return-prevention-alert/*.ts, role audit 2026-10-29). */
export const ALERT_ACTION_PERMISSION = "RETURN_MANAGER_OVERRIDE" as const;

/**
 * Statuses each action accepts (logics/alert/transitions.ts): a RESOLVED alert is closed (mute and
 * resolve answer 409 error.return.alert_already_resolved); acknowledging twice is pointless; an
 * acknowledged or muted alert can still be muted or resolved (O147).
 */
const ALERT_ACTION_STATUSES: Record<AlertAction, readonly AlertStatus[]> = {
  acknowledge: ["ACTIVE"],
  mute       : ["ACTIVE", "ACKNOWLEDGED", "MUTED"],
  resolve    : ["ACTIVE", "ACKNOWLEDGED", "MUTED"],
};

/**
 * `stateOf(action, status)` → `{ enabled, reason }`. Not available = aria-disabled with the reason
 * (permission code first, then status), never hidden.
 */
export function useAlertActions() {
  const { t } = useTranslation(["returns", "common"]);
  const { hasAccess } = usePermission(ALERT_ACTION_PERMISSION);

  const stateOf = useCallback(
    (action: AlertAction, status: AlertStatus): { enabled: boolean; reason: string | null } => {
      if (!hasAccess) return { enabled: false, reason: t("common:permission.required", { code: ALERT_ACTION_PERMISSION }) };
      if (!ALERT_ACTION_STATUSES[action].includes(status)) {
        const key = status === "RESOLVED" ? "alertActions.reason.resolved" : "alertActions.reason.alreadyAcknowledged";
        return { enabled: false, reason: t(`returns:${key}`) };
      }
      return { enabled: true, reason: null };
    },
    [hasAccess, t],
  );

  return { stateOf, hasAccess };
}

/** Backend error keys (error.return.*) are translated; anything else shows the modal's own fallback. */
function reportError(e: unknown, fallback: string) {
  const msg = (e as Error)?.message;
  if (typeof msg === "string" && msg.startsWith("error.")) message.error(msg);
  else notifications.show({ color: "red", title: fallback, message: fallback });
}

// ─── Acknowledge ────────────────────────────────────────────────────────────

interface AcknowledgeAlertModalProps {
  opened   : boolean;
  onClose  : () => void;
  alertId  : string;
  onSuccess: () => void;
}

export function AcknowledgeAlertModal({ opened, onClose, alertId, onSuccess }: AcknowledgeAlertModalProps) {
  const { t } = useTranslation("returns");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await ReturnPreventionAlertApi.acknowledge(alertId);
      notifications.show({ color: "teal", title: t("trustDetail.successTitle"), message: t("alertActions.acknowledge.done") });
      onSuccess();
      onClose();
    } catch (e) {
      reportError(e, t("alertActions.acknowledge.error"));
      onSuccess();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("alertActions.acknowledge.title")} size={440} radius="md">
      <Stack gap="md">
        <Text size="sm" c="var(--nw-text-2)">
          {t("alertActions.acknowledge.confirm")}
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={loading}>
            {t("templateForm.close")}
          </Button>
          <Button onClick={handleConfirm} loading={loading}>
            {t("prevention.detail.acknowledge")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─── Mute ────────────────────────────────────────────────────────────────────

interface MuteAlertModalProps {
  opened   : boolean;
  onClose  : () => void;
  alertId  : string;
  onSuccess: () => void;
}

export function MuteAlertModal({ opened, onClose, alertId, onSuccess }: MuteAlertModalProps) {
  const { t } = useTranslation("returns");
  const [mutedUntil, setMutedUntil] = useState<string | null>(null);
  const [loading, setLoading]       = useState(false);

  const handleConfirm = async () => {
    if (!mutedUntil) return;
    setLoading(true);
    try {
      await ReturnPreventionAlertApi.mute(alertId, new Date(mutedUntil));
      notifications.show({ color: "teal", title: t("trustDetail.successTitle"), message: t("alertActions.mute.done") });
      onSuccess();
      onClose();
      setMutedUntil(null);
    } catch (e) {
      reportError(e, t("alertActions.mute.error"));
      onSuccess();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("alertActions.mute.title")} size={440} radius="md">
      <Stack gap="md">
        <DateTimePicker
          label={t("alertActions.mute.untilLabel")}
          description={t("alertActions.mute.untilNote")}
          placeholder={t("alertActions.mute.untilPlaceholder")}
          valueFormat="YYYY-MM-DD HH:mm"
          value={mutedUntil}
          onChange={setMutedUntil}
          minDate={new Date()}
          required
          clearable
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={loading}>
            {t("templateForm.close")}
          </Button>
          <Button onClick={handleConfirm} loading={loading} disabled={!mutedUntil}>
            {t("prevention.detail.mute")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─── Resolve ─────────────────────────────────────────────────────────────────

interface ResolveAlertModalProps {
  opened   : boolean;
  onClose  : () => void;
  alertId  : string;
  onSuccess: () => void;
}

export function ResolveAlertModal({ opened, onClose, alertId, onSuccess }: ResolveAlertModalProps) {
  const { t } = useTranslation("returns");
  const [notes, setNotes]   = useState("");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await ReturnPreventionAlertApi.resolve(alertId, notes.trim() || undefined);
      notifications.show({ color: "teal", title: t("trustDetail.successTitle"), message: t("alertActions.resolve.done") });
      // O149 — the decision is saved even when the notes column is not migrated yet; say so.
      if (res && res.notesSaved === false) {
        notifications.show({ color: "yellow", title: t("alertActions.resolve.notesNotSavedTitle"), message: t("alertActions.resolve.notesNotSaved") });
      }
      onSuccess();
      onClose();
      setNotes("");
    } catch (e) {
      reportError(e, t("alertActions.resolve.error"));
      onSuccess();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("alertActions.resolve.title")} size={440} radius="md">
      <Stack gap="md">
        <Textarea
          label={t("failedOps.notesOptional")}
          placeholder={t("alertActions.resolve.placeholder")}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          autosize
          minRows={3}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={loading}>
            {t("templateForm.close")}
          </Button>
          <Button onClick={handleConfirm} loading={loading}>
            {t("prevention.detail.resolve")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
